import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'

// Layout Components
import MainLayout from '@/components/layouts/MainLayout'
import AuthLayout from '@/components/layouts/AuthLayout'

// Authentication Pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import SetPasswordPage from '@/pages/auth/SetPasswordPage'

// Customer Portal Pages
import DashboardPage from '@/pages/customer/DashboardPage'
import InvestmentsPage from '@/pages/customer/InvestmentsPage'
import TransactionsPage from '@/pages/customer/TransactionsPage'
import ProfilePage from '@/pages/customer/ProfilePage'
import DocumentsPage from '@/pages/customer/DocumentsPage'

// Admin Panel Pages
import AdminDashboard from '@/pages/admin/AdminDashboard'
import UserManagement from '@/pages/admin/UserManagement'
import BankManagement from '@/pages/admin/BankManagement'
import SystemSettings from '@/pages/admin/SystemSettings'
import AdminAgentManagement from '@/pages/admin/AgentManagement'
import TeamsManagement from '@/pages/admin/TeamsManagement'
import VoIPSettings from '@/pages/admin/VoIPSettings'
import AdminInvestments from '@/pages/admin/AdminInvestments'
import AdminConversations from '@/pages/admin/AdminConversations'

// Call Center Pages
import CallCenterDashboard from '@/pages/callcenter/CallCenterDashboard'
import CallManagement from '@/pages/callcenter/CallManagement'
import AdminConsole from '@/pages/callcenter/AdminConsole'
import AgentCustomers from '@/pages/callcenter/AgentCustomers'
import CallCenterConversations from '@/pages/callcenter/Conversations'
import CallCenterCustomerAdmin from '@/pages/callcenter/CustomerAdmin'
import CallCenterStaffAdmin from '@/pages/callcenter/StaffAdmin'

// Public Pages
import HomePage from '@/pages/public/HomePage'
import AboutPage from '@/pages/public/AboutPage'
import ContactPage from '@/pages/public/ContactPage'
import ImpressumPage from '@/pages/public/ImpressumPage'
import DatenschutzPage from '@/pages/public/DatenschutzPage'
import AnlageberatungPage from '@/pages/public/AnlageberatungPage'
import AltersvorsorgeePage from '@/pages/public/AltersvorsorgeePage'
import FinanzierungenPage from '@/pages/public/FinanzierungenPage'
import FinanzberatungFrauenPage from '@/pages/public/FinanzberatungFrauenPage'
import AdvisoryVermoegenPage from '@/pages/public/AdvisoryVermoegenPage'
import NachhaltigeGeldanlagenPage from '@/pages/public/NachhaltigeGeldanlagenPage'
import StellenAngebotePage from '@/pages/public/StellenAngebotePage'

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) => {
  const { isAuthenticated, user } = useAuthStore()
  
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />
  }
  
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

function App() {
  return (
    <div className="min-h-screen bg-background">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/anlageberatung" element={<AnlageberatungPage />} />
        <Route path="/altersvorsorge" element={<AltersvorsorgeePage />} />
        <Route path="/finanzierungen" element={<FinanzierungenPage />} />
        <Route path="/finanzberatung-frauen" element={<FinanzberatungFrauenPage />} />
        <Route path="/advisory" element={<AdvisoryVermoegenPage />} />
        <Route path="/nachhaltige-geldanlagen" element={<NachhaltigeGeldanlagenPage />} />
        <Route path="/stellen" element={<StellenAngebotePage />} />
        
        {/* Authentication Routes */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/set-password" element={<SetPasswordPage />} />
        
        {/* Customer Portal Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DashboardPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/investments"
          element={
            <ProtectedRoute>
              <MainLayout>
                <InvestmentsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TransactionsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DocumentsPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin Panel Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <AdminDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <UserManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/banks"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <BankManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/investments"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <AdminInvestments />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/conversations"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <AdminConversations />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <SystemSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agents"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <AdminAgentManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/teams"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <TeamsManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/voip-settings"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <MainLayout>
                <VoIPSettings />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Call Center Routes */}
        <Route
          path="/callcenter"
          element={
            <ProtectedRoute allowedRoles={['admin', 'staff', 'callcenter_admin']}>
              <MainLayout>
                <CallCenterDashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Yönetim Konsolu sayfası kaldırıldı */}
        <Route
          path="/callcenter/musteriler"
          element={
            <ProtectedRoute allowedRoles={['staff', 'callcenter_admin', 'admin']}>
              <MainLayout>
                <AgentCustomers />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/callcenter/gorusmeler"
          element={
            <ProtectedRoute allowedRoles={['staff', 'callcenter_admin', 'admin']}>
              <MainLayout>
                <CallCenterConversations />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/callcenter/musteri-yonetimi"
          element={
            <ProtectedRoute allowedRoles={['callcenter_admin', 'admin']}>
              <MainLayout>
                <CallCenterCustomerAdmin />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/callcenter/personel-yonetimi"
          element={
            <ProtectedRoute allowedRoles={['callcenter_admin', 'admin']}>
              <MainLayout>
                <CallCenterStaffAdmin />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/callcenter/calls"
          element={
            <ProtectedRoute allowedRoles={['admin', 'staff']}>
              <MainLayout>
                <CallManagement />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  )
}

export default App 